
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Journalist } from '@/components/reader-home/types';
import { User } from '@supabase/supabase-js';

export const useJournalistFollowService = (
  setFollowedJournalists: React.Dispatch<React.SetStateAction<Journalist[]>>
) => {
  const loadFollowedJournalists = useCallback(async (user: User | null) => {
    if (!user) {
      setFollowedJournalists([]);
      return;
    }
    
    try {
      console.log('Loading followed journalists for user:', user.id);
      
      // Get journalists the user follows
      const { data: follows, error: followsError } = await supabase
        .from('journalist_follows')
        .select('journalist_id')
        .eq('user_id', user.id);
      
      if (followsError) throw followsError;
      
      console.log('Found follows:', follows);
      
      if (follows && follows.length > 0) {
        const journalistIds = follows.map(follow => follow.journalist_id);
        console.log('Journalist IDs to fetch:', journalistIds);
        
        // Get profiles for those journalists
        const { data: journalistProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .in('id', journalistIds);
          
        if (profilesError) throw profilesError;
        
        console.log('Found journalist profiles:', journalistProfiles);
        
        // Transform to the expected format
        const formattedJournalists: Journalist[] = (journalistProfiles || []).map(profile => ({
          id: profile.id,
          name: profile.display_name || 'Unknown',
          avatar: profile.avatar_url || '',
          bio: profile.bio || '',
          followers: 0, // We could fetch this separately if needed
          topics: [], // We could add this later
          unread: 0 // We could implement this with a notification system
        }));
        
        console.log('Formatted journalists:', formattedJournalists);
        setFollowedJournalists(formattedJournalists);
      } else {
        console.log('No follows found for user');
        setFollowedJournalists([]);
      }
    } catch (err) {
      console.error('Error fetching followed journalists:', err);
      setFollowedJournalists([]);
    }
  }, [setFollowedJournalists]);

  // Follow/unfollow a journalist
  const toggleFollowJournalist = async (journalistId: string) => {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to follow journalists',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      console.log('Toggle follow for journalist:', journalistId);
      
      // Check if already following
      const { data: existingFollow } = await supabase
        .from('journalist_follows')
        .select('*')
        .eq('user_id', user.id)
        .eq('journalist_id', journalistId);
      
      console.log('Existing follow:', existingFollow);
      
      if (existingFollow && existingFollow.length > 0) {
        // Unfollow
        const { error } = await supabase
          .from('journalist_follows')
          .delete()
          .eq('user_id', user.id)
          .eq('journalist_id', journalistId);
          
        if (error) throw error;
        
        toast({
          title: 'Unfollowed',
          description: 'You have unfollowed this journalist',
        });
      } else {
        // Follow
        const { error } = await supabase
          .from('journalist_follows')
          .insert({
            user_id: user.id,
            journalist_id: journalistId,
          });
          
        if (error) throw error;
        
        toast({
          title: 'Following',
          description: 'You are now following this journalist',
        });
      }
      
      // Reload the journalist list
      await loadFollowedJournalists(user);
    } catch (err) {
      console.error('Error toggling follow status:', err);
      toast({
        title: 'Action failed',
        description: 'There was a problem updating your follow status',
        variant: 'destructive'
      });
    }
  };

  return { loadFollowedJournalists, toggleFollowJournalist };
};
